import { apiClient } from '@/api/client';
import type { WorkflowStep } from '@/api/workflows';
import { isDevLoginEnabled } from '@/auth/devLogin';

import { advisorSourceKeyOf } from './advisorPrefill';

/**
 * Re-reading the advisor's representative codes from GAMX while the form is open.
 *
 * The codes on the session are a snapshot taken at ISAM login: `POST /auth/refresh` rotates
 * tokens but never re-reads the profile, and the BFF replays the stored codes as claims for up
 * to the absolute session cap. A code granted or withdrawn mid-session is therefore invisible —
 * both to the dropdown's options and to `decideAdvisorPrefill`'s "is this still one of THEIR
 * codes?" test, which would be validating against a stale list.
 *
 * So the runtime re-queries on STEP ENTRY: each time the advisor lands on a step carrying a
 * rep-code dropdown, the codes are read afresh and published to the session cache. The policy
 * that reacts to the new list is not duplicated here — see `decideAdvisorPrefill`.
 */

/**
 * True when this step authors a field that renders as a representative-code dropdown.
 *
 * Asks {@link advisorSourceKeyOf} rather than reading the field properties directly: the key it
 * returns already folds BOTH spellings of the field — the `advisorRepCodeSelector` SingleSelect
 * and a plain field with `sourceValue: 'advisor.repCode'`, which FieldRenderer also renders as a
 * Select for a multi-code advisor — into one answer, so this can't drift from what actually
 * puts a dropdown on the screen.
 *
 * Repeatable instances are not walked: the question is whether such a field is AUTHORED here,
 * and every instance of one shares the single session-wide list of codes.
 */
export function stepNeedsRepCodes(step: WorkflowStep): boolean {
  if (step.type !== 'DynamicForm') return false;
  return step.sections.some((section) =>
    section.rows.some((row) =>
      row.columns.some((col) => col.field && advisorSourceKeyOf(col.field) === 'advisor.repCode'),
    ),
  );
}

/**
 * The in-flight refresh, shared by any caller that asks while it is running.
 *
 * Deduplication only — deliberately NOT a time-based throttle. "Refresh when they land on the
 * field" means a real re-query per visit, so a step the advisor navigates back onto asks again.
 * This just stops one navigation that renders twice from becoming two GAMX calls.
 */
let _inFlight: Promise<string[] | null> | null = null;

/**
 * Re-reads the session's representative codes from the BFF.
 *
 * @returns the current codes, or null when the refresh could not be completed — the caller
 * leaves the session's existing codes alone on null. The endpoint itself already answers with
 * the stored codes rather than an error when GAMX is unreachable or unhelpful, so null here
 * means the call didn't land at all (offline, 401 on an ended session). Either way the
 * dropdown is never emptied by a failure.
 */
export function refreshAdvisorRepCodes(): Promise<string[] | null> {
  // Dev Login mints its identity from a header, not a BFF session cookie (see
  // `DevLoginIdentities`), so the route would answer 401 on every step entry and there would
  // be no GAMX profile behind it anyway. Same reason the session timer skips POST /auth/refresh.
  if (isDevLoginEnabled()) return Promise.resolve(null);
  if (_inFlight) return _inFlight;
  _inFlight = apiClient
    // `baseURL: '/'` because the BFF's auth routes sit outside the `/api/v1` group —
    // same convention as the session timer's POST /auth/refresh.
    .post<{ representativeCodes: string[] }>('/auth/advisor/rep-codes/refresh', null, {
      baseURL: '/',
    })
    .then((res) => res.data.representativeCodes ?? null)
    .catch(() => null)
    .finally(() => {
      _inFlight = null;
    });
  return _inFlight;
}

/** Test seam — resets the in-flight dedupe between cases. Not for app code. */
export const advisorRepCodeRefreshInternals = {
  reset: () => {
    _inFlight = null;
  },
};
